import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchReportEndOfDay } from '@/lib/api/reports.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportFilters } from '@/pages/reports/report-filters-context.js';

function todayIso() {
  const n = new Date();
  return `${n.getUTCFullYear()}-${String(n.getUTCMonth() + 1).padStart(2, '0')}-${String(n.getUTCDate()).padStart(2, '0')}`;
}

export function EndOfDayReportPage() {
  const { storeId } = useReportFilters();
  const [date, setDate] = useState(todayIso);

  const qs = useMemo(() => {
    const q: { date: string; storeId?: string } = { date };
    if (storeId.trim()) q.storeId = storeId.trim();
    return q;
  }, [date, storeId]);

  const q = useQuery({
    queryKey: ['reports-eod', qs],
    queryFn: () => fetchReportEndOfDay(qs),
  });

  const d = q.data;

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading end of day…</div>;
  if (q.isError || !d) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load end of day report.</div>;

  const print = () => window.print();

  return (
    <div>
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <label className="flex items-center gap-2 text-sm">
          <span className="font-medium text-slate-600">Business date (UTC)</span>
          <input type="date" className="rounded-lg border border-slate-300 px-2 py-1" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button type="button" className="no-print rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-95" onClick={print}>
          🖨 Print Report
        </button>
      </div>

      <div className="printable-report eod-print-root space-y-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm print:border-0 print:shadow-none">
        <header className="border-b border-slate-100 pb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">End of day</p>
          <h1 className="text-2xl font-bold text-slate-950">{d.storeName}</h1>
          <p className="mt-1 text-sm text-slate-600">{d.date}</p>
        </header>

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="Tickets opened" value={d.tickets.opened} />
          <Metric label="Tickets closed" value={d.tickets.closed} />
          <Metric label="In progress (pipeline)" value={d.tickets.inProgress} />
          <Metric label="Cancelled" value={d.tickets.cancelled} />
        </section>

        <section>
          <h2 className="text-sm font-bold uppercase text-slate-500">Revenue</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="Total" value={formatCurrency(d.revenue.total)} />
            <Metric label="Cash" value={formatCurrency(d.revenue.cash)} />
            <Metric label="Card" value={formatCurrency(d.revenue.card)} />
            <Metric label="Store credit" value={formatCurrency(d.revenue.storeCredit)} />
            <Metric label="Deposits" value={formatCurrency(d.revenue.deposits)} />
            <Metric label="Refunds (gross)" value={formatCurrency(d.revenue.refunds)} />
            <Metric label="Net" value={formatCurrency(d.revenue.net)} emphasize />
          </div>
        </section>

        {d.tips ? (
          <section>
            <h2 className="text-sm font-bold uppercase text-slate-500">Tips (not revenue)</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Metric label="Total tips" value={formatCurrency(d.tips.total)} />
              <Metric label="Cash tips" value={formatCurrency(d.tips.cash)} />
              <Metric label="Card tips" value={formatCurrency(d.tips.card)} />
              <Metric label="Tip count" value={d.tips.count} />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Cash tips are in the drawer and increase expected cash. Tips are excluded from P&amp;L revenue.
            </p>
          </section>
        ) : null}

        <section className="grid gap-6 lg:grid-cols-2">
          <div>
            <h2 className="text-sm font-bold uppercase text-slate-500">Cash drawer</h2>
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              <li>Status: {d.cashDrawer.status.replace(/_/g, ' ')}</li>
              <li>Opened: {d.cashDrawer.opened ? 'Yes' : 'No'}</li>
              <li>Opening float: {d.cashDrawer.openingFloat != null ? formatCurrency(d.cashDrawer.openingFloat) : '—'}</li>
              <li>Expected cash: {d.cashDrawer.expectedCash != null ? formatCurrency(d.cashDrawer.expectedCash) : '—'}</li>
              <li>
                Cash tips in drawer:{' '}
                {d.cashDrawer.cashTipsInDrawer != null
                  ? formatCurrency(d.cashDrawer.cashTipsInDrawer)
                  : '—'}
              </li>
              <li>Closing count: {d.cashDrawer.closingCount != null ? formatCurrency(d.cashDrawer.closingCount) : '—'}</li>
              <li>Variance: {d.cashDrawer.variance != null ? formatCurrency(d.cashDrawer.variance) : '—'}</li>
            </ul>
          </div>
          <div>
            <h2 className="text-sm font-bold uppercase text-slate-500">Technicians</h2>
            <ul className="mt-2 space-y-1 text-sm">
              {d.technicians.map((t, i) => (
                <li key={t.name + i}>
                  <span className="font-medium">{t.name}</span> — {t.ticketsClosed} closed, {t.laborMinutes} min labor
                </li>
              ))}
              {!d.technicians.length ? <li className="text-slate-500">No closed tickets with logged time.</li> : null}
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-bold uppercase text-slate-500">Top repairs</h2>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase text-slate-500">
                <th className="py-2">Service</th>
                <th className="py-2">Count</th>
                <th className="py-2">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {d.topRepairs.map((r, i) => (
                <tr key={r.name + i} className="border-t border-slate-100">
                  <td className="py-2">{r.name}</td>
                  <td className="py-2">{r.count}</td>
                  <td className="py-2">{formatCurrency(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

function Metric({ label, value, emphasize }: { label: string; value: string | number; emphasize?: boolean }) {
  return (
    <div className={`rounded-xl border border-slate-100 p-3 ${emphasize ? 'border-primary bg-indigo-50/40' : 'bg-slate-50/50'}`}>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-bold text-slate-950">{value}</p>
    </div>
  );
}
